/*  PendingForm.jsx  */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select as MuiSelect,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { fetchJourneys } from "../../../../features/journey/journeyThunk";
import InitializeJourney from "./InializeJourney";

const PendingForm = ({ selectedJourney, tempJourney, onClearJourney, onEditStops }) => {
  const [journey, setJourney] = useState({
    journeyId: "",
    journeyState: "CREATED",
    journeyName: "",
    journeyType: "PICKING",
    thingId: "",
  });

  const [isInitOpen, setIsInitOpen] = useState(false);
  const [isChangeThingOpen, setIsChangeThingOpen] = useState(false);
  const [newThingId, setNewThingId] = useState("");

  const dispatch = useDispatch();
  const selectedVehicle = useSelector((state) => state.vehicle.isVehicle);
  const { data: journeysData = {} } = useSelector((state) => state.journey || {});
  const tempData = journeysData.tempJourneys || [];

  /* --------------------------------------------------------------- */
  /* 1. Populate form from tempData using tempJourney (preferred)   */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    const source = tempJourney || selectedJourney;

    if (source && source.journeyId && tempData.length > 0) {
      const found = tempData.find((item) => item.journeyId === source.journeyId) || source;

      setJourney({
        journeyId: found.journeyId || "",
        journeyState: found.state || "CREATED",
        journeyName: found.journeyName || "",
        journeyType: found.type || "PICKING",
        thingId: found.thingId || "",
      });
    } else {
      setJourney({
        journeyId: "",
        journeyState: "CREATED",
        journeyName: "",
        journeyType: "PICKING",
        thingId: selectedVehicle || "",
      });
    }
  }, [selectedJourney, tempJourney, tempData, selectedVehicle]);

  /* --------------------------------------------------------------- */
  /* 2. Input handlers                                               */
  /* --------------------------------------------------------------- */
  const handleInput = (e) => {
    const { name, value } = e.target;
    setJourney((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (e) => {
    const { name, value } = e.target;
    setJourney((prev) => ({ ...prev, [name]: value }));
  };

  /* --------------------------------------------------------------- */
  /* 3. API Calls                                                    */
  /* --------------------------------------------------------------- */
  const handleSubmit = async () => {
    const payload = {
      thingId: selectedVehicle,
      journeyId: journey.journeyId,
      journeyName: journey.journeyName,
      type: journey.journeyType,
    };

    try {
      const url = selectedJourney
        ? "https://v3.dhundhoo.com/journey/updateJourney"
        : "https://v3.dhundhoo.com/journey/createJourney";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": window.localStorage.getItem("token") || "session",
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Journey updated successfully");
        onClearJourney();
      } else {
        const err = await res.text();
        toast.error("Update failed: " + err);
      }
    } catch (err) {
      toast.error("Network error");
    }
    dispatch(fetchJourneys(selectedVehicle));
  };

  const handleReInit = () => setIsInitOpen(true);
  const handleClear = () => onClearJourney();

  const handleChangeThing = () => {
    setIsChangeThingOpen(true);
    setNewThingId("");
  };

  const handleDelete = async () => {
    if (!journey.journeyId) return toast.error("No journey selected");

    if (!window.confirm("Are you sure you want to delete this journey?")) return;

    try {
      const res = await fetch(
        `https://v3.dhundhoo.com/journey/remove/${journey.journeyId}`,
        {
          method: "GET",
          headers: { "x-dhundhoo-session": window.localStorage.getItem("token") },
        }
      );
      if (res.ok) {
        toast.success("Journey deleted");
        dispatch(fetchJourneys(selectedVehicle));
        onClearJourney();
      } else {
        toast.error("Delete failed");
      }
    } catch {
      toast.error("Delete error");
    }
  };

  const handleChangeThingConfirm = async () => {
    if (!newThingId.trim()) return toast.error("Enter a valid thing ID");

    try {
      const res = await fetch(`https://v3.dhundhoo.com/journey/changeThing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": window.localStorage.getItem("token"),
        },
        body: JSON.stringify({ thingId: newThingId, journeyId: journey.journeyId }),
      });
      if (res.ok) {
        toast.success("Thing ID changed");
        setIsChangeThingOpen(false);
        dispatch(fetchJourneys(selectedVehicle));
      } else {
        toast.error("Failed to change thing");
      }
    } catch {
      toast.error("Change thing error");
    }
  };

  const handleInitSuccess = () => {
    setIsInitOpen(false);
    toast.success("Journey initialized");
    dispatch(fetchJourneys(selectedVehicle));
  };
  const handleBack = () => setIsInitOpen(false);

  /* --------------------------------------------------------------- */
  /* 4. Render                                                       */
  /* --------------------------------------------------------------- */
  if (isInitOpen) {
    return (
      <InitializeJourney
        thingId={journey.thingId || selectedVehicle || "TEST12345"}
        onInitializeSuccess={handleInitSuccess}
        onBack={handleBack}
        selectedJourney={tempJourney || selectedJourney}
      />
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "#fff",
        p: 3,
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        maxHeight: "100vh",
        overflowY: "auto",
      }}
    >
      <Typography variant="h6" align="center" mb={3}>
        {selectedJourney
          ? `Update Journey - ${journey.journeyName} (${journey.thingId})`
          : "Create New Journey"}
      </Typography>

      {selectedJourney && (
        <>
          <Box mb={2}>
            <Typography variant="caption">Journey Id:</Typography>
            <TextField value={journey.journeyId} disabled fullWidth />
          </Box>

          <Box mb={2}>
            <Typography variant="caption">* Journey State:</Typography>
            <TextField value={journey.journeyState} disabled fullWidth />
          </Box>
        </>
      )}

      <Box mb={2}>
        <Typography variant="caption">* Journey Name:</Typography>
        <TextField
          name="journeyName"
          value={journey.journeyName}
          onChange={handleInput}
          fullWidth
          placeholder="e.g. TESTING JOURNEY"
        />
      </Box>

      <Box mb={2}>
        <Typography variant="caption">* Journey Type:</Typography>
        <MuiSelect
          name="journeyType"
          value={journey.journeyType}
          onChange={handleSelect}
          sx={{ height: "40px" }}
          fullWidth
        >
          <MenuItem value="PICKING">PICKING</MenuItem>
          <MenuItem value="DROPPING">DROPPING</MenuItem>
        </MuiSelect>
      </Box>

      {selectedJourney && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <Button variant="contained" color="info" onClick={handleSubmit}>
            UPDATE JOURNEY
          </Button>

          <Button
            variant="contained"
            color="info"
            onClick={() => onEditStops(tempJourney)}
          >
            Edit stops
          </Button>

          <Button variant="contained" color="info" onClick={handleReInit}>
            Re-Initialize
          </Button>

          <Button variant="contained" color="info" onClick={handleClear}>
            Clear
          </Button>

          <Button variant="contained" color="error" onClick={handleChangeThing}>
            Change Thing
          </Button>

          <Button variant="contained" color="error" onClick={handleDelete}>
            Remove
          </Button>
        </Box>
      )}

      <Dialog open={isChangeThingOpen} onClose={() => setIsChangeThingOpen(false)}>
        <DialogTitle>Move Journey to other thing</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New thing id"
            type="text"
            fullWidth
            value={newThingId}
            onChange={(e) => setNewThingId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsChangeThingOpen(false)}
            color="info"
            variant="contained"
          >
            Cancel
          </Button>
          <Button onClick={handleChangeThingConfirm} color="info" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingForm;